import { NO_BAG, type BagRefusal, type BagSnapshot, type BagSource } from "./snapshot.ts";

/**
 * What every view of the page's bag listens to (P2.8-T11): the snapshot, replaced on each change and kept otherwise, as
 * `useSyncExternalStore` needs, the listeners told after each change, and the listeners told of each refusal the
 * server sends. The bag store (`store.ts`) builds on it and is the only thing that changes the snapshot.
 */
export abstract class BagListeners implements BagSource {
  private readonly listeners = new Set<() => void>();
  private readonly refusalListeners = new Set<(refusal: BagRefusal) => void>();
  protected snapshot: BagSnapshot = NO_BAG;

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window reads, the same object until something changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): BagSnapshot => this.snapshot;

  /**
   * Listens for the server's refusals, each told after the store has taken it.
   *
   * @param listener Called with each refusal.
   * @returns A function that stops listening.
   */
  public onRefused(listener: (refusal: BagRefusal) => void): () => void {
    this.refusalListeners.add(listener);
    return () => {
      this.refusalListeners.delete(listener);
    };
  }

  /**
   * Tells every refusal listener of a refusal the store has taken.
   *
   * @param refusal The refusal.
   */
  protected refused(refusal: BagRefusal): void {
    for (const listener of this.refusalListeners) {
      listener(refusal);
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The snapshot.
   */
  protected change(next: BagSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
